import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getAdminEffectiveCompanyOrGardenerIdNum } from '../../lib/adminViewAs';
import {
	getTeamAttendance,
	getGardenerAttendanceHistory,
	type TeamMemberAttendance,
	type AttendanceHistoryEntry
} from '@services/attendance';
import {
	getMyCompanyMembers,
	inviteToCompany,
	isCanSendMarketplaceOffersOn,
	setCompanyMemberOffersPermission,
	type CompanyMember
} from '@services/companies';
import { buildAppUrl } from '@utils/appBase';
import { createGardenerInvite } from '@services/gardenerInvites';
import { getUsers, type UserResponse } from '@services/users';
import { useI18n } from '../../i18n';

function formatTime(iso: string | null): string {
	if (!iso) return '—';
	return new Date(iso).toLocaleTimeString('en-GB', {
		hour: '2-digit',
		minute: '2-digit'
	});
}

function StatusBadge({
	status,
	copy
}: {
	status: TeamMemberAttendance['status'];
	copy: (en: string, de: string, hr: string) => string;
}) {
	const variants: Record<string, string> = {
		checked_in: 'bg-success',
		checked_out: 'bg-secondary',
		not_checked_in: 'bg-light text-dark'
	};
	const labels: Record<string, string> = {
		checked_in: copy('Checked in', 'Eingecheckt', 'Prijavljen'),
		checked_out: copy('Checked out', 'Ausgecheckt', 'Odjavljen'),
		not_checked_in: copy('Not checked in', 'Nicht eingecheckt', 'Nije prijavljen')
	};
	return (
		<span className={`badge ${variants[status] ?? 'bg-secondary'}`}>
			{labels[status] ?? status}
		</span>
	);
}

function openWhatsAppShare(text: string) {
	if (!text.trim()) return;
	const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
	window.open(url, '_blank', 'noopener,noreferrer');
}

export default function TeamPage() {
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const [roster, setRoster] = useState<CompanyMember[]>([]);
	const [companyId, setCompanyId] = useState<number | null>(null);
	const [companyName, setCompanyName] = useState<string | null>(null);
	const [team, setTeam] = useState<TeamMemberAttendance[]>([]);
	const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
	const [checkedInCount, setCheckedInCount] = useState(0);
	const [loading, setLoading] = useState(true);
	const [rosterLoading, setRosterLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [teamMessage, setTeamMessage] = useState<string | null>(null);

	// Local filters for better team overview
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState<'all' | 'checked_in' | 'checked_out' | 'not_checked_in'>('all');

	// Invite gardener modal (company only)
	const [inviteModalOpen, setInviteModalOpen] = useState(false);
	const [gardenersForInvite, setGardenersForInvite] = useState<UserResponse[]>([]);
	const [inviteGardenerId, setInviteGardenerId] = useState<number | ''>('');
	const [inviteSearch, setInviteSearch] = useState('');
	const [inviteLoading, setInviteLoading] = useState(false);
	const [inviteError, setInviteError] = useState<string | null>(null);
	const [inviteMode, setInviteMode] = useState<'existing' | 'new'>('existing');
	const [createdInviteCode, setCreatedInviteCode] = useState<string | null>(null);
	const [createdInviteLink, setCreatedInviteLink] = useState<string | null>(null);
	const [inviteActionMessage, setInviteActionMessage] = useState<string | null>(null);

	// Gardener history modal
	const [selectedGardenerId, setSelectedGardenerId] = useState<number | null>(null);
	const [selectedGardenerName, setSelectedGardenerName] = useState<string>('');
	const [history, setHistory] = useState<AttendanceHistoryEntry[] | null>(null);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyError, setHistoryError] = useState<string | null>(null);
	const [historyRangeType, setHistoryRangeType] = useState<'this_week' | 'this_month' | 'last_month' | 'custom'>('this_month');
	const [historyFrom, setHistoryFrom] = useState<string | null>(null);
	const [historyTo, setHistoryTo] = useState<string | null>(null);
	const [updatingOffersMemberId, setUpdatingOffersMemberId] = useState<number | null>(null);

	const teamContextUserId = useMemo(() => {
		const lens = getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget);
		if (user?.role === 'admin' && lens != null) return String(lens);
		return user?.id ? String(user.id) : '';
	}, [user, viewAsTarget]);

	useEffect(() => {
		if (!teamContextUserId) return;
		setRosterLoading(true);
		const coId =
			user?.activeCompanyId != null && Number.isFinite(Number(user?.activeCompanyId))
				? Number(user.activeCompanyId)
				: undefined;
		getMyCompanyMembers(teamContextUserId, coId)
			.then((res) => {
				setRoster(res.members || []);
				setCompanyId(res.company?.id ?? null);
				setCompanyName(res.company?.name ?? null);
			})
			.catch(() => setRoster([]))
			.finally(() => setRosterLoading(false));
	}, [teamContextUserId, user?.activeCompanyId]);

	// Load gardeners when opening invite modal (company only, exclude current members)
	useEffect(() => {
		if (!inviteModalOpen || !teamContextUserId || (user?.role !== 'company' && !(user?.role === 'admin' && viewAsTarget?.role === 'company')) || !companyId) return;
		const memberIds = new Set(roster.map((m) => m.id));
		getUsers({ role: 'gardener' })
			.then((list) => setGardenersForInvite(list.filter((g) => !memberIds.has(g.id))))
			.catch(() => setGardenersForInvite([]));
		setInviteGardenerId('');
		setInviteSearch('');
		setInviteError(null);
		setInviteMode('existing');
		setCreatedInviteCode(null);
		setCreatedInviteLink(null);
		setInviteActionMessage(null);
	}, [inviteModalOpen, teamContextUserId, user?.role, viewAsTarget, companyId, roster]);

	useEffect(() => {
		if (!teamContextUserId) return;
		setLoading(true);
		setError(null);
		getTeamAttendance(teamContextUserId, date)
			.then((res) => {
				setTeam(res.team);
				setCheckedInCount(res.checked_in_count);
				setTeamMessage(res.message ?? null);
			})
			.catch((e) => setError(e instanceof Error ? e.message : 'Failed to load team'))
			.finally(() => setLoading(false));
	}, [teamContextUserId, date]);

	const combinedRows = useMemo(() => {
		const byEmail = new Map<string, TeamMemberAttendance>();
		for (const member of team) {
			byEmail.set(member.email.toLowerCase(), member);
		}
		return roster.map((member) => {
			const attendance = byEmail.get(member.email.toLowerCase());
			return {
				id: member.id,
				company_member_id: member.company_member_id,
				user_role: member.user_role,
				can_send_marketplace_offers: member.can_send_marketplace_offers,
				name: member.name,
				email: member.email,
				role: member.member_role,
				company_name: attendance?.company_name ?? companyName,
				status: attendance?.status ?? 'not_checked_in',
				check_in_at: attendance?.check_in_at ?? null,
				check_out_at: attendance?.check_out_at ?? null
			};
		});
	}, [roster, team, companyName]);

	const filteredRows = useMemo(
		() =>
			combinedRows.filter((row) => {
				if (!search && statusFilter === 'all') return true;
				const matchesSearch =
					!search ||
					row.name.toLowerCase().includes(search.toLowerCase()) ||
					row.email.toLowerCase().includes(search.toLowerCase());
				const matchesStatus = statusFilter === 'all' ? true : row.status === statusFilter;
				return matchesSearch && matchesStatus;
			}),
		[combinedRows, search, statusFilter]
	);

	const showCompanyColumn = useMemo(() => {
		const companies = new Set(
			combinedRows
				.map((r) => r.company_name ?? '')
				.filter((name) => name && name.trim().length > 0)
		);
		return companies.size > 1;
	}, [combinedRows]);

	const filteredGardenersForInvite = useMemo(() => {
		const q = inviteSearch.trim().toLowerCase();
		if (!q) return gardenersForInvite;
		return gardenersForInvite.filter((g) => `${g.name} ${g.email}`.toLowerCase().includes(q));
	}, [gardenersForInvite, inviteSearch]);
	const getInviteGardenerLabel = (g: UserResponse) => `${g.name} (${g.email})`;

	function computeRange(rangeType: 'this_week' | 'this_month' | 'last_month' | 'custom', baseDate: string) {
		const base = new Date(baseDate);
		if (Number.isNaN(base.getTime())) {
			return { from: baseDate, to: baseDate };
		}

		if (rangeType === 'this_week') {
			const day = base.getDay(); // 0 (Sun) - 6 (Sat)
			const diffToMonday = (day + 6) % 7; // convert so Monday is start
			const fromDate = new Date(base);
			fromDate.setDate(base.getDate() - diffToMonday);
			const toDate = new Date(fromDate);
			toDate.setDate(fromDate.getDate() + 6);
			return {
				from: fromDate.toISOString().slice(0, 10),
				to: toDate.toISOString().slice(0, 10)
			};
		}

		if (rangeType === 'last_month') {
			const year = base.getFullYear();
			const month = base.getMonth(); // 0-11
			const firstOfThisMonth = new Date(year, month, 1);
			const lastOfLastMonth = new Date(firstOfThisMonth);
			lastOfLastMonth.setDate(0); // day 0 of this month = last day of previous month
			const firstOfLastMonth = new Date(lastOfLastMonth.getFullYear(), lastOfLastMonth.getMonth(), 1);
			return {
				from: firstOfLastMonth.toISOString().slice(0, 10),
				to: lastOfLastMonth.toISOString().slice(0, 10)
			};
		}

		if (rangeType === 'custom' && historyFrom && historyTo) {
			return { from: historyFrom, to: historyTo };
		}

		// this_month default
		const year = base.getFullYear();
		const month = base.getMonth();
		const first = new Date(year, month, 1);
		const last = new Date(year, month + 1, 0);
		return {
			from: first.toISOString().slice(0, 10),
			to: last.toISOString().slice(0, 10)
		};
	}

	async function loadHistory(gardenerId: number, baseDate: string, rangeTypeOverride?: 'this_week' | 'this_month' | 'last_month' | 'custom') {
		const type = rangeTypeOverride ?? historyRangeType;
		const { from, to } = computeRange(type, baseDate);
		setHistoryLoading(true);
		setHistoryError(null);
		try {
			const res = await getGardenerAttendanceHistory(
				gardenerId,
				from,
				to,
				companyId ?? user?.activeCompanyId ?? user?.companyId ?? null
			);
			setHistory(res.entries);
			setHistoryFrom(from);
			setHistoryTo(to);
			setHistoryRangeType(type);
		} catch (e: any) {
			setHistoryError(e instanceof Error ? e.message : 'Failed to load attendance history');
			setHistory(null);
		} finally {
			setHistoryLoading(false);
		}
	}

	if (!user) return null;
	if (user.role !== 'company' && user.role !== 'admin') {
		return (
			<div className="alert alert-info">
				{copy('Team attendance is only available for companies and admins.', 'Team-Anwesenheit ist nur für Unternehmen und Admins verfügbar.', 'Prisustvo tima dostupno je samo tvrtkama i administratorima.')}
			</div>
		);
	}

	return (
		<>
			<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
				<div className="d-flex align-items-center gap-3 flex-wrap">
					<h1 className="h3 mb-0">{copy('Team', 'Team', 'Tim')}</h1>
					{user.role === 'company' && companyId && (
						<button
							type="button"
							className="btn btn-success btn-sm"
							onClick={() => setInviteModalOpen(true)}
						>
							<i className="bi bi-person-plus me-1"></i>
							{copy('Invite gardener', 'Gärtner einladen', 'Pozovi vrtlara')}
						</button>
					)}
				</div>
				<div className="d-flex align-items-center gap-2">
					<label className="form-label mb-0 small text-muted">{copy('Date', 'Datum', 'Datum')}</label>
					<div className="input-group input-group-sm" style={{ maxWidth: '190px' }}>
						<input
							type="date"
							className="form-control"
							value={date}
							onChange={(e) => setDate(e.target.value)}
						/>
						<button
							type="button"
							className="btn btn-outline-secondary"
							onClick={() => {
								const el = document.querySelector<HTMLInputElement>('input[type="date"]');
								el?.showPicker?.();
							}}
							title={copy('Open calendar', 'Kalender öffnen', 'Otvori kalendar')}
						>
							<i className="bi bi-calendar3" />
						</button>
					</div>
				</div>
			</div>

			{error && (
				<div className="alert alert-danger" role="alert">
					{error}
				</div>
			)}

			{/* Team overview + today's attendance in a single table */}
			<div className="card mb-4">
				<div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
					<div>
						<span className="fw-bold d-block">{copy("Team & today's attendance", 'Team & heutige Anwesenheit', 'Tim i današnja prisutnost')}</span>
						<small className="text-muted d-block">
							{copy('Gardeners need permission here to send marketplace offers; company accounts always can.', 'Gärtner benötigen hier die Berechtigung, Marktplatz-Angebote zu senden; Unternehmenskonten können dies immer.', 'Vrtlari ovdje trebaju dozvolu za slanje ponuda na Marketplace; računi tvrtki to uvijek mogu.')}
						</small>
						<small className="text-muted d-block">
							{checkedInCount} of {team.length} checked in
							{team.length > 0 && (
								<>
									{' '}
									· {Math.round((checkedInCount / team.length) * 100)}%
								</>
							)}
							{companyName && ` · ${companyName}`}
						</small>
						{teamMessage && <div className="text-muted small mt-1">{teamMessage}</div>}
					</div>
					<div className="d-flex flex-wrap align-items-center gap-2">
						<input
							type="search"
							className="form-control form-control-sm"
							placeholder={copy('Search name or email…', 'Name oder E-Mail suchen…', 'Pretraži ime ili e-poštu…')}
							style={{ minWidth: '200px' }}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<select
							className="form-select form-select-sm"
							style={{ minWidth: '160px' }}
							value={statusFilter}
							onChange={(e) =>
								setStatusFilter(e.target.value as 'all' | 'checked_in' | 'checked_out' | 'not_checked_in')
							}
						>
							<option value="all">{copy('All statuses', 'Alle Status', 'Svi statusi')}</option>
							<option value="checked_in">{copy('Checked in', 'Eingecheckt', 'Prijavljen')}</option>
							<option value="checked_out">{copy('Checked out', 'Ausgecheckt', 'Odjavljen')}</option>
							<option value="not_checked_in">{copy('Not checked in', 'Nicht eingecheckt', 'Nije prijavljen')}</option>
						</select>
						<button
							type="button"
							className="btn btn-outline-secondary btn-sm"
							onClick={() => {
								if (!user?.id) return;
								setLoading(true);
								setError(null);
								getTeamAttendance(user.id, date)
									.then((res) => {
										setTeam(res.team);
										setCheckedInCount(res.checked_in_count);
										setTeamMessage(res.message ?? null);
									})
									.catch((e) =>
										setError(e instanceof Error ? e.message : 'Failed to refresh team attendance')
									)
									.finally(() => setLoading(false));
							}}
						>
							<i className="bi bi-arrow-clockwise" />
						</button>
					</div>
				</div>
				<div className="card-body p-0">
					{rosterLoading || loading ? (
						<div className="p-4 text-center text-muted">
							<div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
							<span className="ms-2">Loading team…</span>
						</div>
					) : combinedRows.length === 0 ? (
						<div className="p-4 text-center text-muted">
							<i className="bi bi-people fs-4 d-block mb-2"></i>
							<p className="mb-0 small">No team members yet. Approved gardeners in your company will appear here.</p>
						</div>
					) : (
						<div className="table-responsive">
							<table className="table table-hover align-middle mb-0">
								<thead className="table-light">
									<tr>
											<th>{copy('Name', 'Name', 'Ime')}</th>
											<th>{copy('Email', 'E-Mail', 'E-pošta')}</th>
											<th>{copy('Role', 'Rolle', 'Uloga')}</th>
										{(user.role === 'company' || user.role === 'admin') && (
											<th className="text-nowrap">{copy('Marketplace offers', 'Marktplatz-Angebote', 'Marketplace ponude')}</th>
										)}
										{showCompanyColumn && <th>{copy('Company', 'Unternehmen', 'Tvrtka')}</th>}
										<th>{copy('Status today', 'Status heute', 'Status danas')}</th>
										<th>{copy('Check-in', 'Prijava', 'Prijava')}</th>
										<th>{copy('Check-out', 'Odjava', 'Odjava')}</th>
									</tr>
								</thead>
								<tbody>
									{filteredRows.map((row) => (
										<tr
											key={row.id}
											style={{ cursor: 'pointer' }}
											onClick={async () => {
														setSelectedGardenerId(row.id);
														setSelectedGardenerName(row.name);
														setHistory(null);
														setHistoryError(null);
														await loadHistory(row.id, date, 'this_month');
													}}
										>
											<td>
												<span className="fw-medium text-success text-decoration-none">
													{row.name}
												</span>
											</td>
											<td>
												<small className="text-muted">{row.email}</small>
											</td>
											<td>
												<span className="badge bg-success-subtle text-success">{row.role}</span>
											</td>
											{(user.role === 'company' || user.role === 'admin') && (
												<td
													onClick={(e) => e.stopPropagation()}
													onMouseDown={(e) => e.stopPropagation()}
													onKeyDown={(e) => e.stopPropagation()}
													role="presentation"
												>
													{row.user_role === 'gardener' && row.company_member_id != null ? (
														<div
															className="form-check form-switch mb-0"
															onClick={(e) => e.stopPropagation()}
															onMouseDown={(e) => e.stopPropagation()}
														>
															<input
																id={`offers-${row.company_member_id}`}
																className="form-check-input"
																type="checkbox"
																checked={isCanSendMarketplaceOffersOn(row.can_send_marketplace_offers)}
																disabled={
																	updatingOffersMemberId !== null &&
																	Number(updatingOffersMemberId) === Number(row.company_member_id)
																}
																onClick={(e) => e.stopPropagation()}
																onMouseDown={(e) => e.stopPropagation()}
																onChange={async (e) => {
																	if (!user?.id || row.company_member_id == null) return;
																	const enabled = e.target.checked;
																	const cmId = Number(row.company_member_id);
																	setUpdatingOffersMemberId(cmId);
																	try {
																		await setCompanyMemberOffersPermission(user.id, cmId, enabled);
																		const res = await getMyCompanyMembers(user.id);
																		setRoster(res.members || []);
																		setError(null);
																	} catch (err) {
																		setError(err instanceof Error ? err.message : 'Could not update permission');
																	} finally {
																		setUpdatingOffersMemberId(null);
																	}
																}}
																aria-label={`Allow ${row.name} to send marketplace offers`}
															/>
														</div>
													) : (
														<span className="text-muted small">—</span>
													)}
												</td>
											)}
											{showCompanyColumn && <td>{row.company_name || '—'}</td>}
											<td>
												<StatusBadge status={row.status} copy={copy} />
											</td>
											<td>{formatTime(row.check_in_at)}</td>
											<td>{formatTime(row.check_out_at)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>

			{/* Invite gardener modal */}
			{inviteModalOpen && user.role === 'company' && companyId && (
				<div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2100 }}>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">{copy('Invite gardener', 'Gärtner einladen', 'Pozovi vrtlara')}</h5>
								<button type="button" className="btn-close" onClick={() => setInviteModalOpen(false)} aria-label={copy('Close', 'Schließen', 'Zatvori')}></button>
							</div>
							<div className="modal-body">
								{inviteError && (
									<div className="alert alert-danger py-2 mb-3">{inviteError}</div>
								)}
								<div className="btn-group w-100 mb-3" role="tablist">
									<button
										type="button"
										className={`btn btn-sm ${inviteMode === 'existing' ? 'btn-success' : 'btn-outline-success'}`}
										onClick={() => {
											setInviteMode('existing');
											setInviteError(null);
											setCreatedInviteCode(null);
											setCreatedInviteLink(null);
											setInviteActionMessage(null);
										}}
										disabled={inviteLoading}
									>
										{copy('Invite existing gardener', 'Vorhandenen Gärtner einladen', 'Pozovi postojećeg vrtlara')}
									</button>
									<button
										type="button"
										className={`btn btn-sm ${inviteMode === 'new' ? 'btn-success' : 'btn-outline-success'}`}
										onClick={() => {
											setInviteMode('new');
											setInviteError(null);
											setCreatedInviteCode(null);
											setCreatedInviteLink(null);
											setInviteActionMessage(null);
										}}
										disabled={inviteLoading}
									>
										{copy('Invite by link/code', 'Einladen putem Linka/Coda', 'Pozovi putem linka/koda')}
									</button>
								</div>

								{inviteMode === 'existing' ? (
									<>
										<label className="form-label">{copy('Select a gardener to invite', 'Wählen Sie einen Gärtner zum Einladen', 'Odaberite vrtlara za poziv')}</label>
										<input
											list="team-invite-gardener-options"
											type="text"
											className="form-control"
											placeholder={copy('Type to search and pick gardener…', 'Zum Suchen und Auswählen eines Gärtners tippen…', 'Upišite za pretragu i odabir vrtlara…')}
											value={inviteSearch}
											onChange={(e) => {
												const nextValue = e.target.value;
												setInviteSearch(nextValue);
												const normalized = nextValue.trim().toLowerCase();
												const exact = gardenersForInvite.find((g) => {
													const label = getInviteGardenerLabel(g).toLowerCase();
													return label === normalized || g.email.toLowerCase() === normalized;
												});
												setInviteGardenerId(exact ? exact.id : '');
												setInviteError(null);
											}}
											disabled={inviteLoading}
										/>
										<datalist id="team-invite-gardener-options">
											{filteredGardenersForInvite.map((g) => (
												<option key={g.id} value={getInviteGardenerLabel(g)} />
											))}
										</datalist>
										{inviteSearch.trim() && filteredGardenersForInvite.length === 0 && gardenersForInvite.length > 0 && (
											<p className="text-muted small mt-2 mb-0">{copy('No gardeners match your search.', 'Keine Gärtner passen zu Ihrer Suche.', 'Nijedan vrtlar ne odgovara pretraživanju.')}</p>
										)}
										{gardenersForInvite.length === 0 && (
											<p className="text-muted small mt-2 mb-0">{copy('All gardeners are already in your company or have a pending request/invite.', 'Alle Gärtner sind bereits in Ihrer Firma oder haben eine ausstehende Anfrage/Einladung.', 'Svi vrtlari su već u vašoj tvrtki ili imaju zahtjev/poziv na čekanju.')}</p>
										)}
									</>
								) : (
									<>
										<p className="text-muted small mb-3">
											{copy(
												'Generate a link/code you can send to a gardener who has not created an account yet. They can register from your link and then accept the invite to join your company.',
												'Erstellen Sie einen Link oder Code, den Sie an einen Gärtner senden können, der noch kein Konto erstellt hat. Er kann sich über Ihren Link registrieren und dann die Einladung zu Ihrem Unternehmen annehmen.',
												'Izradite poveznicu ili kod koji možete poslati vrtlaru koji još nije otvorio račun. Može se registrirati putem vaše poveznice, a zatim prihvatiti poziv za pridruživanje vašoj tvrtki.'
											)}
										</p>
										{createdInviteCode && (
											<div className="alert alert-success small mb-0">
												<div className="mb-2">
													<strong>{copy('Invite created!', 'Einladung erstellt!', 'Pozivnica je izrađena!')}</strong> {copy('Code:', 'Code:', 'Kod:')}{' '}
													<code className="fw-bold">{createdInviteCode}</code>
												</div>
												{createdInviteLink && (
													<div className="d-flex flex-wrap gap-2 align-items-center">
														<code style={{ wordBreak: 'break-all' }}>{createdInviteLink}</code>
														<button
															type="button"
															className="btn btn-sm btn-outline-success"
															onClick={async () => {
																if (!createdInviteLink) return;
																await navigator.clipboard?.writeText(createdInviteLink);
																setInviteActionMessage(copy('Link copied', 'Link kopiert', 'Poveznica je kopirana'));
																window.setTimeout(() => setInviteActionMessage(null), 2200);
															}}
														>
															{copy('Copy link', 'Link kopieren', 'Kopiraj poveznicu')}
														</button>
														<button
															type="button"
															className="btn btn-sm btn-success"
															onClick={() => {
																if (!createdInviteLink || !createdInviteCode) return;
																const text = copy(
																	`Join our company on HortEra.\n\nUse this invite link:\n${createdInviteLink}\n\nInvite code: ${createdInviteCode}`,
																	`Treten Sie unserem Unternehmen auf HortEra bei.\n\nVerwenden Sie diesen Einladungslink:\n${createdInviteLink}\n\nEinladungscode: ${createdInviteCode}`,
																	`Pridružite se našoj tvrtki na HortEri.\n\nUpotrijebite ovu poveznicu pozivnice:\n${createdInviteLink}\n\nKod pozivnice: ${createdInviteCode}`
																);
																openWhatsAppShare(text);
																setInviteActionMessage(copy('Opening WhatsApp...', 'WhatsApp wird geöffnet...', 'Otvara se WhatsApp...'));
																window.setTimeout(() => setInviteActionMessage(null), 2200);
															}}
														>
															<i className="bi bi-whatsapp me-1" />
															{copy('Share on WhatsApp', 'Per WhatsApp teilen', 'Podijeli na WhatsAppu')}
														</button>
														{inviteActionMessage && (
															<div className="text-success small w-100">{inviteActionMessage}</div>
														)}
													</div>
												)}
											</div>
										)}
									</>
								)}
							</div>
							<div className="modal-footer">
								<button type="button" className="btn btn-outline-secondary" onClick={() => setInviteModalOpen(false)}>
									{copy('Cancel', 'Abbrechen', 'Odustani')}
								</button>
								<button
									type="button"
									className="btn btn-success"
									disabled={(inviteMode === 'existing' ? inviteGardenerId === '' : false) || inviteLoading}
									onClick={async () => {
										if (!user?.id) return;
										setInviteLoading(true);
										setInviteError(null);
										try {
											if (inviteMode === 'existing') {
												if (inviteGardenerId === '') return;
												await inviteToCompany(user.id, companyId, inviteGardenerId);
												setInviteModalOpen(false);
												const res = await getMyCompanyMembers(user.id);
												setRoster(res.members || []);
											} else {
												const created = await createGardenerInvite({
													userId: Number(user.id)
												});
												setCreatedInviteCode(created.code);
												// Mirror the "Invite client" pattern: use the public homepage URL that preserves the code into /login.
												const link = buildAppUrl(`?gardenerInvite=${encodeURIComponent(created.code)}`);
												setCreatedInviteLink(link);
											}
										} catch (e: any) {
											setInviteError(e.message || copy('Failed to send invite', 'Senden der Einladung fehlgeschlagen', 'Slanje pozivnice nije uspjelo'));
										} finally {
											setInviteLoading(false);
										}
									}}
								>
									{inviteLoading
										? (inviteMode === 'existing'
											? copy('Sending…', 'Senden…', 'Slanje…')
											: copy('Creating…', 'Erstellen…', 'Stvaranje…'))
										: inviteMode === 'existing'
											? copy('Send invite', 'Einladung senden', 'Pošalji poziv')
											: copy('Create invite', 'Einladung erstellen', 'Kreiraj poziv')}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Gardener attendance history modal */}
			{selectedGardenerId !== null && (
				<div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2100 }}>
					<div className="modal-dialog modal-lg modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">{copy('Attendance history', 'Anwesenheitsverlauf', 'Povijest prisutnosti')} · {selectedGardenerName}</h5>
								<button
									type="button"
									className="btn-close"
									onClick={() => {
										setSelectedGardenerId(null);
										setHistory(null);
										setHistoryError(null);
									}}
									aria-label={copy('Close', 'Schließen', 'Zatvori')}
								></button>
							</div>
							<div className="modal-body">
								<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
									<div className="btn-group btn-group-sm" role="group" aria-label="History range">
										<button
											type="button"
											className={`btn ${
												historyRangeType === 'this_week' ? 'btn-success text-white' : 'btn-outline-success'
											}`}
											onClick={() => {
												if (!selectedGardenerId) return;
												loadHistory(selectedGardenerId, date, 'this_week');
											}}
										>
											{copy('This week', 'Diese Woche', 'Ovaj tjedan')}
										</button>
										<button
											type="button"
											className={`btn ${
												historyRangeType === 'this_month' ? 'btn-success text-white' : 'btn-outline-success'
											}`}
											onClick={() => {
												if (!selectedGardenerId) return;
												loadHistory(selectedGardenerId, date, 'this_month');
											}}
										>
											{copy('This month', 'Dieser Monat', 'Ovaj mjesec')}
										</button>
										<button
											type="button"
											className={`btn ${
												historyRangeType === 'last_month' ? 'btn-success text-white' : 'btn-outline-success'
											}`}
											onClick={() => {
												if (!selectedGardenerId) return;
												loadHistory(selectedGardenerId, date, 'last_month');
											}}
										>
											{copy('Last month', 'Letzter Monat', 'Prošli mjesec')}
										</button>
									</div>
									<button
										type="button"
										className={`btn btn-sm ${
											historyRangeType === 'custom' ? 'btn-success text-white' : 'btn-outline-success'
										}`}
										onClick={() => setHistoryRangeType('custom')}
									>
										{copy('Custom range', 'Benutzerdefinierter Zeitraum', 'Prilagođeni raspon')}
									</button>
									{historyRangeType === 'custom' && (
										<div className="d-flex flex-wrap align-items-center gap-2 ms-2">
											<div className="input-group input-group-sm" style={{ maxWidth: '170px' }}>
												<span className="input-group-text">{copy('From', 'Von', 'Od')}</span>
												<input
													type="date"
													className="form-control"
													value={historyFrom ?? ''}
													onChange={(e) => setHistoryFrom(e.target.value || null)}
												/>
											</div>
											<div className="input-group input-group-sm" style={{ maxWidth: '170px' }}>
												<span className="input-group-text">{copy('To', 'Bis', 'Do')}</span>
												<input
													type="date"
													className="form-control"
													value={historyTo ?? ''}
													onChange={(e) => setHistoryTo(e.target.value || null)}
												/>
											</div>
											<button
												type="button"
												className="btn btn-sm btn-success"
												disabled={!selectedGardenerId || !historyFrom || !historyTo}
												onClick={() => {
													if (!selectedGardenerId || !historyFrom || !historyTo) return;
													loadHistory(selectedGardenerId, date, 'custom');
												}}
											>
												{copy('Apply', 'Anwenden', 'Primijeni')}
											</button>
										</div>
									)}
								</div>
								{historyLoading && (
									<div className="text-center text-muted py-3">
										<div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
										<span className="ms-2">{copy('Loading history…', 'Verlauf wird geladen…', 'Učitavanje povijesti…')}</span>
									</div>
								)}
								{historyError && (
									<div className="alert alert-danger py-2 mb-3">
										{historyError}
									</div>
								)}
								{!historyLoading && !historyError && history && history.length === 0 && (
									<p className="text-muted small mb-0">{copy('No attendance records found for the last 30 days.', 'Keine Anwesenheitseinträge für die letzten 30 Tage gefunden.', 'Nema zapisa prisutnosti za posljednjih 30 dana.')}</p>
								)}
								{!historyLoading && !historyError && history && history.length > 0 && (
									<div className="table-responsive">
										<table className="table table-sm align-middle">
												<thead className="table-light">
													<tr>
														<th>{copy('Date', 'Datum', 'Datum')}</th>
														<th>{copy('Status', 'Status', 'Status')}</th>
														<th>{copy('Check-in', 'Prijava', 'Prijava')}</th>
														<th>{copy('Check-out', 'Odjava', 'Odjava')}</th>
													</tr>
												</thead>
												<tbody>
													{history.map((entry) => (
														<tr key={entry.id}>
															<td>{entry.date}</td>
															<td>
																<StatusBadge status={entry.status} copy={copy} />
															</td>
															<td>{formatTime(entry.check_in_at)}</td>
															<td>{formatTime(entry.check_out_at)}</td>
														</tr>
													))}
												</tbody>
											</table>
									</div>
								)}
							</div>
							<div className="modal-footer">
								<button
									type="button"
									className="btn btn-outline-success me-auto"
									disabled={!history || history.length === 0}
									onClick={() => {
										if (!history || history.length === 0) return;
										const header = 'Date,Status,Check-in,Check-out\n';
										const rows = history
											.map(
												(e) =>
													`${e.date},${e.status},${e.check_in_at ?? ''},${e.check_out_at ?? ''}`
											)
											.join('\n');
										const csv = header + rows;
										const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
										const url = URL.createObjectURL(blob);
										const a = document.createElement('a');
										const safeName = selectedGardenerName.replace(/[^a-z0-9]+/gi, '_') || 'gardener';
										a.href = url;
										a.download = `${safeName}_attendance.csv`;
										document.body.appendChild(a);
										a.click();
										a.remove();
										URL.revokeObjectURL(url);
									}}
								>
									<i className="bi bi-download me-1" />
									{copy('Export CSV', 'CSV exportieren', 'Izvezi CSV')}
								</button>
								<button
									type="button"
									className="btn btn-outline-secondary"
									onClick={() => {
										setSelectedGardenerId(null);
										setHistory(null);
										setHistoryError(null);
									}}
								>
									{copy('Close', 'Schließen', 'Zatvori')}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
